import https from 'https';
import axios, { AxiosResponseHeaders } from 'axios';
import { v4 as generateUuid } from 'uuid';
import { requestLogger } from './loggers';

interface SendHttpReqParams {
  method?: string;
  url: string;
  headers?: any;
  data?: any;
  timeout?: number;
  maxRedirects?: number;
  hasLogs?: boolean;
  hasHeadersLogs?: boolean;
  hasDataLogs?: boolean;
  proxyHttpsAgent?: any;
}

export interface SendHttpResponse {
  status: number;
  headers: AxiosResponseHeaders | Partial<Record<string, string>>;
  data?: any;
  transactionId: string;
}

class SendHttpReqError extends Error {
  public transactionId: string = ""

  constructor(message: string, transactionId: string){
    super(message)
    this.transactionId = transactionId
  }
}

const sendHttpReq = ({
  method = 'get',
  url,
  headers = {},
  data,
  timeout = 60000,
  maxRedirects = 5,
  hasLogs = true,
  hasHeadersLogs = true,
  hasDataLogs = true,
  proxyHttpsAgent = null,
}:SendHttpReqParams): Promise<SendHttpResponse> => {
  const reqHeaders = { ...headers };

  if (data) reqHeaders['content-type'] = 'application/json';

  const transactionId = reqHeaders['x-transaction-id'] || generateUuid();
  reqHeaders['x-transaction-id'] = transactionId;

  const requestInfo = {
    method,
    url,
    headers: reqHeaders,
    data,
    timeout,
    maxRedirects,
  };

  const HIDDEN = 'HIDDEN';

  hasLogs && requestLogger.debug({
    message: 'Sending request..',
    method,
    url: requestInfo.url,
    transactionId,
    headers: hasHeadersLogs ? requestInfo.headers : HIDDEN,
    data: hasDataLogs ? requestInfo.data : HIDDEN,
  });

  let httpsAgent = null;

  if (proxyHttpsAgent) {
    httpsAgent = proxyHttpsAgent;
  } else {
    httpsAgent = new https.Agent({ rejectUnauthorized: false });
  }

  return axios.request({
    ...requestInfo,
    httpsAgent,

  }).then((resp) => {
    const respInfo: SendHttpResponse = {
      status: resp.status,
      headers: resp.headers,
      data: resp.data,
      transactionId,
    };

    hasLogs && requestLogger.info({
      message: 'Request succeeded!',
      statusCode: respInfo.status,
      method,
      requestUrl: url,

      transactionId,
    });

    hasLogs && requestLogger.debug({
      statusCode: respInfo.status,
      requestUrl: requestInfo.url,

      headers: hasHeadersLogs ? respInfo.headers : HIDDEN,
      data: hasDataLogs ? respInfo.data : HIDDEN,

      transactionId,
    });

    return respInfo;
  }).catch((errResp) => {
    if (errResp.response) {
      const respInfo: SendHttpResponse = {
        status: errResp.response.status,
        headers: errResp.response.headers,
        data: errResp.response.data,
        transactionId,
      };

      hasLogs && requestLogger.info({
        message: 'Request fell out of 2XX range',
        statusCode: respInfo.status,
        method,
        requestUrl: url,

        transactionId,
      });

      hasLogs && requestLogger.debug({
        requestUrl: requestInfo.url,
        statusCode: respInfo.status,

        headers: hasHeadersLogs ? respInfo.headers : HIDDEN,
        data: hasDataLogs ? respInfo.data : HIDDEN,

        transactionId,
      });


      return respInfo;
    }

    let errMsg = `Failed to setup request: ${errResp.message}`;
    let statusCode = 500;

    if (errResp.message.match(/timeout/)) {
      errMsg = errResp.message;
      statusCode = -1;
    } else if (errResp.request) {
      errMsg = 'Request sent, no response received';
      statusCode = 504;
    }

    hasLogs && requestLogger.error({
      message: errMsg,
      statusCode,
      method,
      requestUrl: requestInfo.url,

      transactionId,
    });

    const customError = new SendHttpReqError(errMsg, transactionId);

    return Promise.reject(customError);
  });
};

export default sendHttpReq;
